import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound } from "lucide-react";
import type { AskProduct } from "@ttc-spectree/toolbox-contract";
import { Button } from "@/components/ui/button";
import { askScopeKey } from "@/lib/toolbox/ask";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/** The picker's heading over ADO projects. */
export const ASK_ADO_PRODUCTS_HEADING = "ADO projects";

/** The picker's heading over FogBugz projects linked to an ADO project's code. */
export const ASK_FOGBUGZ_PRODUCTS_HEADING = "FogBugz projects";

/** The note shown to someone who may use FogBugz tools but has not saved their own FogBugz key. */
export const ASK_FOGBUGZ_KEY_NOTE =
  "FogBugz projects show here once you save your own FogBugz key in Settings.";

/**
 * Product Q&A's product picker (epic PI-SPEC-SW-E310, features
 * PI-SPEC-SW-1628 and PI-SPEC-SW-1630): one product per conversation, chosen
 * by its display name from `GET /toolbox/ask/products`. Picking another
 * product while a conversation has messages asks first: "Start a new
 * conversation about <product>?" (the old one closes). The question is an
 * inline, announced alert (`role="alert"`) that also describes the select,
 * so focus stays on the select and a screen reader hears the warning.
 *
 * FogBugz projects linked to an ADO project's code (`fogbugz-project` scope,
 * FogBugz ↔ ADO link of ER-1664) are listed under their own heading, after
 * the ADO projects; with no FogBugz product the list has no headings. A
 * FogBugz project that is not linked is never listed by SpecTree; one whose
 * link was removed since is stopped by the app's check with the fixed
 * PRODUCT_NOT_LINKED sentence (the page shows it, never the ask box). Someone
 * who may use FogBugz tools but has no saved FogBugz key gets one plain note
 * with the way to Settings, because SpecTree lists FogBugz products only with
 * the person's own key.
 */
export interface AskProductPickerProps {
  products: readonly AskProduct[];
  /** The chosen product's scope key (`askScopeKey`), or null. */
  selectedKey: string | null;
  /** True while the conversation has messages: switching asks first. */
  hasConversation: boolean;
  onSelect: (product: AskProduct) => void;
  disabled?: boolean;
  /** True when the person holds the `fogbugz` capability somewhere but has no saved FogBugz key. */
  fogbugzKeyMissing?: boolean;
}

function ProductOption({ product }: { product: AskProduct }) {
  return <option value={askScopeKey(product.scope)}>{product.displayName}</option>;
}

export function AskProductPicker({
  products,
  selectedKey,
  hasConversation,
  onSelect,
  disabled,
  fogbugzKeyMissing,
}: AskProductPickerProps) {
  const [pending, setPending] = useState<AskProduct | null>(null);
  const warningId = useId();

  const choose = (key: string) => {
    const product = products.find((candidate) => askScopeKey(candidate.scope) === key);
    if (!product || key === selectedKey) return;
    if (hasConversation) setPending(product);
    else onSelect(product);
  };

  const ado = products.filter((product) => product.scope.kind === "ado-project");
  const fogbugz = products.filter((product) => product.scope.kind === "fogbugz-project");

  return (
    <div className="flex flex-col gap-2" data-testid="ask-product-picker">
      <label className="flex flex-wrap items-center gap-2.5 text-[13px]">
        <span className={cn("font-medium", TOOLBOX_TEXT.faint)}>Product</span>
        <select
          value={selectedKey ?? ""}
          disabled={disabled}
          aria-describedby={pending ? warningId : undefined}
          onChange={(event) => {
            choose(event.target.value);
          }}
          className="h-8 min-w-[220px] rounded-lg border border-[#27272A] bg-[#121215] px-2.5 text-[13px] text-[#FAFAFA] outline-none focus-visible:border-[#3F3F46] disabled:opacity-60"
          data-testid="ask-product-select"
        >
          <option value="" disabled>
            Choose a product…
          </option>
          {fogbugz.length === 0 ? (
            ado.map((product) => <ProductOption key={askScopeKey(product.scope)} product={product} />)
          ) : (
            <>
              {ado.length > 0 && (
                <optgroup label={ASK_ADO_PRODUCTS_HEADING} data-testid="ask-products-ado">
                  {ado.map((product) => (
                    <ProductOption key={askScopeKey(product.scope)} product={product} />
                  ))}
                </optgroup>
              )}
              <optgroup label={ASK_FOGBUGZ_PRODUCTS_HEADING} data-testid="ask-products-fogbugz">
                {fogbugz.map((product) => (
                  <ProductOption key={askScopeKey(product.scope)} product={product} />
                ))}
              </optgroup>
            </>
          )}
        </select>
      </label>
      {fogbugzKeyMissing && (
        <p className={cn("flex flex-wrap items-center gap-2 text-xs", TOOLBOX_TEXT.faint)} data-testid="ask-fogbugz-key-note">
          <KeyRound className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{ASK_FOGBUGZ_KEY_NOTE}</span>
          <Button asChild variant="link" size="sm" className="h-auto p-0 text-xs">
            <Link to="/settings">Open Settings</Link>
          </Button>
        </p>
      )}
      {pending && (
        <div
          role="group"
          aria-label="Start a new conversation"
          className="glass-card flex flex-wrap items-center gap-3 rounded-[10px] px-4 py-3 text-[13px]"
          data-testid="ask-switch-confirm"
        >
          <span id={warningId} role="alert" className="flex-1" data-testid="ask-switch-warning">
            Start a new conversation about {pending.displayName}? This conversation closes and its messages are cleared.
          </span>
          <Button
            size="sm"
            onClick={() => {
              const product = pending;
              setPending(null);
              onSelect(product);
            }}
            data-testid="ask-switch-yes"
          >
            Start new conversation
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setPending(null);
            }}
            data-testid="ask-switch-no"
          >
            Keep this one
          </Button>
        </div>
      )}
    </div>
  );
}
